import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Users, Trash2, Mail, Phone, Loader2, KeyRound, ArrowRightLeft, CheckCircle2, Euro, Copy, HelpCircle, BookOpen, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const money = (n) => `€ ${(Number(n) || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function AdminVenditori() {
  const { user } = useAuth();
  const [reps, setReps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [showMigrate, setShowMigrate] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/sales-reps");
      setReps(r.data || []);
    } catch (e) { toast.error("Errore caricamento venditori"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-6xl mx-auto p-6 md:p-10" data-testid="admin-venditori">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-[#FF5A1F]">
            <Users size={14} /> Team vendite
          </div>
          <h1 className="mt-2 font-display text-4xl md:text-5xl uppercase text-[#0A0A0A]">Venditori</h1>
          <p className="mt-2 text-sm text-[#0A0A0A]/60 max-w-xl">Ogni venditore ha il proprio "Il mio giorno" con schede, contratti e Google Calendar isolati. Puoi impersonare un venditore dalla sua card qui sotto.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowGuide(true)} className="px-3 py-2 border-2 border-[#0A0A0A]/40 text-xs uppercase tracking-widest hover:border-[#FF5A1F] hover:text-[#FF5A1F] flex items-center gap-1" data-testid="btn-guide-venditore">
            <HelpCircle size={12} /> Guida
          </button>
          <button onClick={() => setShowCreate(true)} className="btn-cta" data-testid="btn-new-venditore">
            <UserPlus size={14} /> Nuovo venditore
          </button>
        </div>
      </header>

      {showGuide && <GuideVenditoreModal onClose={() => setShowGuide(false)} />}

      {loading && reps.length === 0 && (
        <div className="text-sm text-[#0A0A0A]/50 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Carico...</div>
      )}
      {!loading && reps.length === 0 && (
        <div className="border-2 border-dashed border-[#0A0A0A]/30 p-10 text-center text-[#0A0A0A]/60">
          <Users size={32} className="mx-auto mb-3 text-[#0A0A0A]/30" />
          <div className="font-display text-xl uppercase mb-2">Nessun venditore</div>
          <div className="text-sm">Crea il primo venditore per iniziare a distribuire le call.</div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {reps.map((r) => (
          <div key={r.id} className="border-2 border-[#0A0A0A] bg-[#FAFAF7] p-5" data-testid={`rep-card-${r.id}`}>
            <div className="flex items-start justify-between gap-3 mb-4">
              <div className="flex items-start gap-3 min-w-0">
                <div className="w-12 h-12 rounded-full bg-[#FF5A1F] text-white flex items-center justify-center font-display text-xl uppercase shrink-0">
                  {(r.name || r.email).slice(0, 1)}
                </div>
                <div className="min-w-0">
                  <div className="font-display text-lg uppercase text-[#0A0A0A] truncate">{r.name || "—"}</div>
                  <div className="text-xs text-[#0A0A0A]/60 flex items-center gap-1"><Mail size={11} /> {r.email}</div>
                  {r.phone && <div className="text-xs text-[#0A0A0A]/60 flex items-center gap-1 mt-0.5"><Phone size={11} /> {r.phone}</div>}
                </div>
              </div>
              <ImpersonateButton rep={r} />
            </div>
            <div className="grid grid-cols-3 gap-2 text-center border-t-2 border-[#0A0A0A]/10 pt-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#0A0A0A]/50">Contratti</div>
                <div className="font-display text-2xl text-[#0A0A0A]">{r.contratti_totali}</div>
                <div className="text-[9px] text-green-700">{r.contratti_firmati} firmati</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#0A0A0A]/50">Fatturato</div>
                <div className="font-display text-lg text-[#0A0A0A]">{money(r.fatturato_firmato)}</div>
                <div className="text-[9px] text-[#0A0A0A]/40">solo firmati</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#0A0A0A]/50">Provvigioni</div>
                <div className="font-display text-lg text-[#FF5A1F]">{money(r.provvigioni_maturate)}</div>
                <div className="text-[9px] text-[#0A0A0A]/40">maturate</div>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-[#0A0A0A]/10 flex justify-between items-center">
              <SetupInstructionsButton rep={r} />
              <ResetPasswordButton rep={r} />
            </div>
          </div>
        ))}
      </div>

      {showCreate && <CreateVenditoreModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
      {showMigrate && <MigrateModal reps={reps} onClose={() => setShowMigrate(false)} onDone={() => { setShowMigrate(false); load(); }} />}
    </div>
  );
}

function ImpersonateButton({ rep }) {
  const [busy, setBusy] = useState(false);
  const goTo = (path) => {
    setBusy(true);
    try { sessionStorage.setItem("perla.impersonatingSalesRep", JSON.stringify({ id: rep.id, name: rep.name, email: rep.email })); }
    catch (e) {}
    window.location.href = path;
  };
  return (
    <div className="flex flex-col gap-1">
      <button onClick={() => goTo("/admin/mio-giorno")} disabled={busy} className="px-3 py-1.5 border-2 border-[#FF5A1F] bg-[#FF5A1F] text-white text-[10px] uppercase tracking-widest font-bold hover:bg-[#0A0A0A] hover:border-[#0A0A0A] flex items-center gap-1.5 disabled:opacity-60" data-testid={`impersonate-mg-${rep.id}`}>
        {busy ? <Loader2 size={11} className="animate-spin" /> : <Users size={11} />}
        Apri agenda
      </button>
      <button onClick={() => goTo("/admin/contratti")} disabled={busy} className="px-3 py-1.5 border-2 border-[#0A0A0A]/40 text-[#0A0A0A] text-[10px] uppercase tracking-widest font-bold hover:border-[#FF5A1F] hover:text-[#FF5A1F] flex items-center gap-1.5 disabled:opacity-60" data-testid={`impersonate-contratti-${rep.id}`}>
        Apri contratti
      </button>
    </div>
  );
}

function SetupInstructionsButton({ rep }) {
  const [open, setOpen] = useState(false);
  const loginUrl = `${window.location.origin}/admin/login`;
  const setupUrl = `${window.location.origin}/admin/setup`;
  const message = `Ciao ${rep.name},
Ecco le istruzioni per configurare il tuo account Perla Music CRM:

1) ACCEDI
Vai su ${loginUrl}
Email: ${rep.email}
Password: (quella che ti ho comunicato a parte)

2) COLLEGA GOOGLE CALENDAR
Vai su "Setup account" nel menu → sezione Google Calendar → clicca "Collega Google Calendar"
IMPORTANTE: nella schermata Google spunta TUTTE le caselle di consenso, in particolare "Visualizza gli eventi dei tuoi calendari Google", altrimenti l'agenda non caricherà nulla.

3) SALVA IL TUO NUMERO WHATSAPP
Sempre in "Setup account" → sezione WhatsApp → inserisci il tuo numero con prefisso (+39...) e clicca Salva.
I messaggi partiranno dal tuo device WhatsApp, il numero è solo di riferimento.

4) CAMBIA LA PASSWORD
"Setup account" → sezione Password → cambia la password temporanea con una tua a piacere.

5) INIZIA A LAVORARE
- "Home": lista lead da ricontattare, in ritardo per primi
- "Il mio giorno": agenda giorno per giorno con ricerca contatti e frecce di navigazione
- "Contratti": genera contratti dal template Word, invia via WhatsApp con un click

Per qualsiasi dubbio scrivimi.
Alessio`;

  const copy = () => { navigator.clipboard?.writeText(message); toast.success("Istruzioni copiate — incolla in WhatsApp/Email"); };

  return (
    <>
      <button onClick={() => setOpen(true)} className="px-3 py-1.5 border-2 border-blue-600 text-blue-600 text-[10px] uppercase tracking-widest font-bold hover:bg-blue-600 hover:text-white flex items-center gap-1.5" data-testid={`setup-guide-${rep.id}`}>
        📖 Guida setup
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white border-2 border-[#0A0A0A] max-w-2xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="setup-guide-modal">
            <div className="p-5 border-b-2 border-[#0A0A0A] flex items-center justify-between">
              <div className="font-display text-2xl uppercase">Guida setup — {rep.name}</div>
              <button onClick={() => setOpen(false)} className="text-[#0A0A0A]/60 hover:text-[#FF5A1F]">✕</button>
            </div>
            <div className="p-5 space-y-4">
              <div className="text-sm text-[#0A0A0A]/60">
                Copia il testo qui sotto e invialo al venditore via WhatsApp o email. Contiene tutte le istruzioni per collegare Google Calendar, WhatsApp e cambiare la password.
              </div>
              <textarea
                readOnly
                value={message}
                className="w-full h-72 border-2 border-[#0A0A0A] p-3 text-xs font-mono bg-[#FAFAF7] resize-none"
                data-testid="setup-guide-text"
              />
              <div className="flex gap-2">
                <button onClick={copy} className="btn-cta flex-1" data-testid="copy-setup-guide">
                  <Copy size={14} /> Copia istruzioni
                </button>
                <button onClick={() => setOpen(false)} className="px-4 py-2 border-2 border-[#0A0A0A]/40 text-xs uppercase tracking-widest">Chiudi</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ResetPasswordButton({ rep }) {
  const [busy, setBusy] = useState(false);
  const [newPwd, setNewPwd] = useState(null);

  const reset = async () => {
    if (!window.confirm(`Generare una nuova password per ${rep.name}?\nLa vecchia password sarà invalidata.`)) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/sales-reps/${rep.id}/reset-password`);
      setNewPwd(r.data.new_password);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore reset password");
    } finally { setBusy(false); }
  };

  const copyAll = () => {
    const text = `Ciao ${rep.name},\nEcco le tue credenziali per Perla Music:\nEmail: ${rep.email}\nPassword: ${newPwd}\nAccedi qui: ${window.location.origin}/admin/login`;
    navigator.clipboard?.writeText(text);
    toast.success("Credenziali copiate negli appunti");
  };

  return (
    <>
      <button
        onClick={reset}
        disabled={busy}
        className="px-3 py-1.5 border-2 border-[#0A0A0A]/30 text-[10px] uppercase tracking-widest font-bold hover:border-[#FF5A1F] hover:text-[#FF5A1F] flex items-center gap-1.5 disabled:opacity-60"
        title="Genera una nuova password per questo venditore"
        data-testid={`reset-pwd-${rep.id}`}
      >
        {busy ? <Loader2 size={11} className="animate-spin" /> : <KeyRound size={11} />}
        Reset password
      </button>
      {newPwd && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setNewPwd(null)}>
          <div className="bg-white border-2 border-[#0A0A0A] max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="modal-new-password">
            <div className="p-5 border-b-2 border-[#0A0A0A] flex items-center justify-between">
              <div className="font-display text-xl uppercase flex items-center gap-2 text-green-700">
                <CheckCircle2 size={18} /> Nuova password
              </div>
              <button onClick={() => setNewPwd(null)} className="text-[#0A0A0A]/60 hover:text-[#FF5A1F]">✕</button>
            </div>
            <div className="p-5 space-y-4">
              <div className="text-sm text-[#0A0A0A]/70">
                Nuova password per <b>{rep.name}</b> ({rep.email}):
              </div>
              <div className="bg-yellow-50 border-2 border-yellow-500 p-4 flex items-center justify-between gap-3">
                <code className="font-mono text-xl font-bold text-[#0A0A0A] tracking-wider select-all">{newPwd}</code>
                <button
                  onClick={() => { navigator.clipboard?.writeText(newPwd); toast.success("Password copiata"); }}
                  className="px-3 py-2 border-2 border-[#0A0A0A] text-[10px] uppercase tracking-widest font-bold hover:bg-[#0A0A0A] hover:text-white flex items-center gap-1.5"
                  data-testid="copy-password"
                >
                  <Copy size={12} /> Copia
                </button>
              </div>
              <div className="bg-red-50 border-l-4 border-red-500 p-3 text-[11px] text-red-800">
                <b>⚠️ Salva questa password ora</b> — non potrai più vederla dopo aver chiuso. La vecchia password è stata invalidata.
              </div>
              <button
                onClick={copyAll}
                className="w-full px-4 py-3 bg-[#FF5A1F] text-white text-xs uppercase tracking-widest font-bold hover:bg-[#0A0A0A] flex items-center justify-center gap-2"
                data-testid="copy-full-credentials"
              >
                <Copy size={14} /> Copia messaggio completo (email + password + link)
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function CreateVenditoreModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [commissionPct, setCommissionPct] = useState("10");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!name || !email || !password) { toast.error("Nome, email e password obbligatori"); return; }
    if (password.length < 8) { toast.error("Password minimo 8 caratteri"); return; }
    setSaving(true);
    try {
      await api.post("/crm/users", {
        name, email, phone,
        password,
        role: "operatore",
        commission_pct: parseFloat(commissionPct) || 0,
        active: true,
      });
      toast.success("Venditore creato — condividi le credenziali con lui");
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore creazione");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border-2 border-[#0A0A0A] max-w-lg w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="modal-create-venditore">
        <div className="p-5 border-b-2 border-[#0A0A0A] flex items-center justify-between">
          <div className="font-display text-2xl uppercase">Nuovo venditore</div>
          <button onClick={onClose} className="text-[#0A0A0A]/60 hover:text-[#FF5A1F]">✕</button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">Nome completo *</label>
            <input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full border-2 border-[#0A0A0A] p-2 text-sm" placeholder="es. Marco Bianchi" data-testid="new-rep-name" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">Email *</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full border-2 border-[#0A0A0A] p-2 text-sm" placeholder="marco@perlamusic.it" data-testid="new-rep-email" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">Cellulare</label>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1 w-full border-2 border-[#0A0A0A] p-2 text-sm" placeholder="+39 ..." data-testid="new-rep-phone" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">Password iniziale *</label>
              <input type="text" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 w-full border-2 border-[#0A0A0A] p-2 text-sm font-mono" placeholder="min. 8 caratteri" data-testid="new-rep-password" />
              <div className="text-[9px] text-[#0A0A0A]/40 mt-1">Il venditore potrà cambiarla al primo accesso</div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">Provvigione %</label>
              <input value={commissionPct} onChange={(e) => setCommissionPct(e.target.value)} className="mt-1 w-full border-2 border-[#0A0A0A] p-2 text-sm" placeholder="10" data-testid="new-rep-commission" />
            </div>
          </div>
        </div>
        <div className="p-4 border-t-2 border-[#0A0A0A] bg-[#F2F2EC] flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border-2 border-[#0A0A0A]/40 text-xs uppercase tracking-widest">Annulla</button>
          <button onClick={submit} disabled={saving} className="btn-cta disabled:opacity-60" data-testid="new-rep-save">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />} Crea venditore
          </button>
        </div>
      </div>
    </div>
  );
}

function MigrateModal({ reps, onClose, onDone }) {
  const [toId, setToId] = useState(reps[0]?.id || "");
  const [busy, setBusy] = useState(false);

  const migrate = async () => {
    if (!toId) { toast.error("Seleziona un venditore destinatario"); return; }
    if (!window.confirm("Tutti i dati attualmente sotto l'account admin (schede, contratti, rubrica, calendario collegato) verranno spostati sul venditore selezionato. Confermi?")) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/migrate-workspace", { to_user_id: toId });
      const moved = r.data.moved || {};
      const total = Object.values(moved).reduce((a, b) => a + b, 0);
      toast.success(`Migrazione completata: ${total} record spostati`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore migrazione");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border-2 border-[#0A0A0A] max-w-lg w-full" onClick={(e) => e.stopPropagation()} data-testid="modal-migrate">
        <div className="p-5 border-b-2 border-[#0A0A0A] font-display text-2xl uppercase">Migra dati admin → venditore</div>
        <div className="p-5 space-y-3 text-sm text-[#0A0A0A]/80">
          <p>Sposta tutti i dati (schede agenda, contratti, rubrica) attualmente sotto l'account admin al workspace di un venditore.</p>
          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-3 text-[11px]">
            <b>Operazione una tantum</b> — utile subito dopo aver creato il primo venditore per non perdere i dati storici.
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">Venditore destinatario</label>
            <select value={toId} onChange={(e) => setToId(e.target.value)} className="mt-1 w-full border-2 border-[#0A0A0A] p-2 text-sm bg-white" data-testid="migrate-target-select">
              {reps.map((r) => <option key={r.id} value={r.id}>{r.name} ({r.email})</option>)}
            </select>
          </div>
        </div>
        <div className="p-4 border-t-2 border-[#0A0A0A] bg-[#F2F2EC] flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border-2 border-[#0A0A0A]/40 text-xs uppercase tracking-widest">Annulla</button>
          <button onClick={migrate} disabled={busy} className="btn-cta disabled:opacity-60" data-testid="migrate-confirm">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <ArrowRightLeft size={14} />} Sposta ora
          </button>
        </div>
      </div>
    </div>
  );
}


// -----------------------------------------------------------------------------
// GuideVenditoreModal — guida passo-passo per creare un nuovo venditore
// -----------------------------------------------------------------------------
function GuideVenditoreModal({ onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="guide-venditore-modal">
      <div className="bg-white border-4 border-[#0A0A0A] max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="border-b-2 border-[#0A0A0A] p-4 bg-[#F2F2EC] flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-[#FF5A1F]">
              <BookOpen size={12} /> Guida rapida
            </div>
            <h2 className="mt-1 font-display text-2xl uppercase text-[#0A0A0A]">Come aggiungere un nuovo venditore</h2>
          </div>
          <button onClick={onClose} className="p-2 border-2 border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white" data-testid="guide-close">
            <X size={14} />
          </button>
        </div>

        <div className="p-6 space-y-6 text-sm text-[#0A0A0A]/85">
          <p className="text-[#0A0A0A]/70 italic">Solo tu (admin) puoi creare venditori. Ogni venditore avrà un CRM separato con le proprie schede lead, contratti e Google Calendar. Segui questi passi:</p>

          <ol className="space-y-4">
            <li className="flex gap-3">
              <span className="shrink-0 w-7 h-7 bg-[#FF5A1F] text-white flex items-center justify-center font-bold">1</span>
              <div className="flex-1 pt-0.5">
                <div className="font-bold uppercase tracking-widest text-xs mb-1">Clicca "Nuovo venditore"</div>
                <div className="text-[#0A0A0A]/70">Il bottone arancione qui in alto a destra. Si apre un piccolo form.</div>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="shrink-0 w-7 h-7 bg-[#FF5A1F] text-white flex items-center justify-center font-bold">2</span>
              <div className="flex-1 pt-0.5">
                <div className="font-bold uppercase tracking-widest text-xs mb-1">Compila i dati</div>
                <ul className="mt-1 space-y-1 text-[#0A0A0A]/70">
                  <li>• <b>Nome completo</b> (es. Mario Rossi)</li>
                  <li>• <b>Email</b> — sarà l'username di login</li>
                  <li>• <b>Password iniziale</b> — il venditore potrà cambiarla dopo (min. 6 caratteri consigliati)</li>
                  <li>• <b>Cellulare</b> (opzionale) — per contattarlo</li>
                </ul>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="shrink-0 w-7 h-7 bg-[#FF5A1F] text-white flex items-center justify-center font-bold">3</span>
              <div className="flex-1 pt-0.5">
                <div className="font-bold uppercase tracking-widest text-xs mb-1">Salva</div>
                <div className="text-[#0A0A0A]/70">Il venditore compare nella lista. Puoi copiare la password con l'icona 📋 e comunicargliela.</div>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="shrink-0 w-7 h-7 bg-[#FF5A1F] text-white flex items-center justify-center font-bold">4</span>
              <div className="flex-1 pt-0.5">
                <div className="font-bold uppercase tracking-widest text-xs mb-1">Comunica al venditore i primi passi</div>
                <div className="text-[#0A0A0A]/70">Digli di accedere con la sua email/password su questo dominio, e di:</div>
                <ul className="mt-1 space-y-1 text-[#0A0A0A]/70">
                  <li>• andare in <b>Setup Account</b></li>
                  <li>• collegare il proprio <b>Google Calendar</b> (per vedere le call automaticamente)</li>
                  <li>• impostare il proprio <b>numero WhatsApp</b> e quello del <b>manager</b> (te) per inoltrare le note vocali</li>
                  <li>• cambiare la password se lo desidera</li>
                </ul>
              </div>
            </li>
          </ol>

          <div className="border-2 border-blue-500 bg-blue-50 p-3 text-xs text-blue-800">
            <div className="font-bold uppercase tracking-widest mb-1 flex items-center gap-1"><ArrowRightLeft size={11}/> Impersonation</div>
            Ogni card venditore ha il bottone "Impersona" — cliccandolo vedi il CRM del venditore come lo vede lui (utile per supporto o supervisione). Un banner arancione ti ricorda che stai impersonando. Cliccalo di nuovo per tornare al tuo account admin.
          </div>

          <div className="border-2 border-red-500 bg-red-50 p-3 text-xs text-red-700">
            <div className="font-bold uppercase tracking-widest mb-1">🔑 Reset password</div>
            Se un venditore dimentica la password, apri la sua card, click sull'icona <KeyRound size={11} className="inline"/> "Reset password" e imposta una nuova password temporanea. Il venditore riceverà il nuovo accesso.
          </div>

          <div className="border-2 border-amber-500 bg-amber-50 p-3 text-xs text-amber-800">
            <div className="font-bold uppercase tracking-widest mb-1">⚠️ Eliminazione</div>
            L'eliminazione di un venditore rimuove il suo accesso ma NON i suoi lead/contratti (restano visibili a te come admin). Meglio disabilitare (non ancora disponibile) invece che eliminare, se possibile.
          </div>
        </div>

        <div className="border-t-2 border-[#0A0A0A] p-4 bg-[#F2F2EC] flex justify-end">
          <button onClick={onClose} className="px-4 py-2 border-2 border-[#0A0A0A] bg-[#0A0A0A] text-white text-xs uppercase tracking-widest hover:bg-[#FF5A1F] hover:border-[#FF5A1F]" data-testid="guide-got-it">
            Ho capito
          </button>
        </div>
      </div>
    </div>
  );
}
